import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { NAV, type NavGroup, type NavLink as NavLinkT } from './NavConfig';
import { countNewCatalogOrders } from '@/features/catalog/api';
import { useAuth } from '@/app/providers/AuthProvider';
import { useToast } from '@/app/providers/ToastProvider';
import { useTenantScope } from '@/app/providers/TenantScopeProvider';
import { buildScopedPath } from '@/app/routes';
import { featureLabel, normalizePlan, planById, planHasFeature } from '@/shared/format/plan';
import { businessTypeMeta } from '@/shared/api/types/tenant';
import { BrandLogo } from '@/shared/ui/BrandLogo';

interface Props {
  collapsed: boolean;
  onUpgrade: () => void;
}

export function Sidebar({ collapsed, onUpgrade }: Props) {
  const { snapshot, logout } = useAuth();
  const { toast } = useToast();
  const scope = useTenantScope();
  const loc = useLocation();
  const planId = normalizePlan(snapshot.plan);
  const planLabel = planById(planId).label;

  const phone = snapshot.phone ?? '';
  const avatarText = phone ? phone.slice(-2) : 'МХ';

  const [query, setQuery] = useState('');

  // Шинэ төлөвтэй каталог захиалгын тоо — left-menu badge.
  const newOrdersQuery = useQuery({
    queryKey: ['catalog-new-order-count', scope.tenantId, scope.companyId],
    queryFn: countNewCatalogOrders,
    enabled: scope.status === 'ok',
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const newOrderCount = newOrdersQuery.data?.Count ?? 0;

  const isLinkActive = (l: NavLinkT) => {
    const href = buildScopedPath(l.routeKey, scope);
    return loc.pathname === href || loc.pathname.startsWith(href + '/');
  };

  // Идэвхтэй маршрут аль бүлэгт байгааг тогтоох.
  const activeGroupId = useMemo(() => {
    for (const g of NAV) if (g.links.some(isLinkActive)) return g.id;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, scope.tenantId, scope.companyId]);

  // Single-open accordion — ямар ч үед зөвхөн нэг бүлэг нээлттэй. Анхдагч нь идэвхтэй бүлэг.
  const [openId, setOpenId] = useState<string | null>(() => activeGroupId);

  // Маршрут солигдоход идэвхтэй бүлгийг нээж бусдыг хаана.
  useEffect(() => {
    setOpenId(activeGroupId);
  }, [activeGroupId]);

  const toggleGroup = (id: string) => setOpenId((cur) => (cur === id ? null : id));

  const q = query.trim().toLowerCase();
  // Backend BusinessType-ыг string ("Karaoke") эсвэл int буцаах боломжтой тул
  // businessTypeMeta-аар тогтвортой numeric value руу normalize хийнэ.
  const bizType =
    scope.status === 'ok' ? businessTypeMeta(scope.companyBusinessType).value : null;
  const groups = useMemo(() => {
    // Эхлээд BusinessType-аар шүүнэ (жишээ: "Өрөө" зөвхөн Караоке үед), дараа нь хайлт.
    const byBiz = NAV.map((g) => ({
      ...g,
      links: g.links.filter(
        (l) => l.requiredBusinessType == null || l.requiredBusinessType === bizType,
      ),
    })).filter((g) => g.links.length > 0);
    if (!q) return byBiz;
    return byBiz
      .map((g) => ({ ...g, links: g.links.filter((l) => l.label.toLowerCase().includes(q)) }))
      .filter((g) => g.links.length > 0);
  }, [q, bizType]);

  // Хайлт идэвхтэй үед бүх бүлгийг нээнэ; үгүй бол төлвөөс / идэвхтэйгээс.
  const isGroupOpen = (g: NavGroup) => !!q || openId === g.id;

  function renderLink(l: NavLinkT) {
    const featureLocked = !!l.requiredFeature && !planHasFeature(planId, l.requiredFeature);
    // Багц-түвшний түгжээ: requiredPlan-аас доош багцад идэвхгүй (serverId rank).
    const planLocked =
      !!l.requiredPlan && planById(planId).serverId < planById(l.requiredPlan).serverId;
    const locked = featureLocked || planLocked;
    if (locked) {
      const reqPlanLabel = l.requiredPlan ? planById(l.requiredPlan).label : '';
      const lockMsg = featureLocked
        ? `'${featureLabel(l.requiredFeature!)}' функц одоогийн багцад идэвхгүй. Багц шинэчилнэ үү.`
        : `Энэ хэсэг '${reqPlanLabel}' багцаас дээш идэвхтэй. Багц шинэчилнэ үү.`;
      const lockTitle = featureLocked
        ? `Энэ функц одоогийн '${planLabel}' багцад идэвхгүй`
        : `Энэ хэсэг '${reqPlanLabel}' багцад л идэвхтэй`;
      return (
        <a
          key={l.routeId}
          href="#"
          className="ts-nav-item ts-nav-locked"
          title={lockTitle}
          onClick={(e) => {
            e.preventDefault();
            toast(lockMsg, 'warning');
            onUpgrade();
          }}
        >
          <span className="ts-nav-icon">
            <i className={`bi bi-${l.icon}`} />
          </span>
          <span className="ts-nav-label">{l.label}</span>
          <span className="ts-nav-lock">
            <i className="bi bi-lock-fill" />
          </span>
        </a>
      );
    }
    const href = buildScopedPath(l.routeKey, scope);
    return (
      <NavLink
        key={l.routeId}
        to={href}
        className={clsx('ts-nav-item', isLinkActive(l) && 'active')}
        style={l.accent ? { color: '#60a5fa' } : {}}
      >
        <span className="ts-nav-icon">
          <i className={`bi bi-${l.icon}`} />
        </span>
        <span className="ts-nav-label">{l.label}</span>
        {l.routeKey === 'catalogOrders' && newOrderCount > 0 ? (
          <span
            title={`${newOrderCount} шинэ захиалга`}
            style={{
              marginLeft: 'auto',
              background: '#ef4444',
              color: '#fff',
              borderRadius: 999,
              fontSize: '0.66rem',
              fontWeight: 700,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {newOrderCount > 99 ? '99+' : newOrderCount}
          </span>
        ) : null}
      </NavLink>
    );
  }

  return (
    <aside className={clsx('ts-sidebar', collapsed && 'collapsed')}>
      <div className="ts-sidebar-brand">
        <BrandLogo variant="icon" size={36} />
        <div className="ts-brand-name">
          Tse<span>Tsu</span>
        </div>
      </div>

      <nav className="ts-sidebar-nav">
        <div className="ts-nav-search">
          <i className="bi bi-search" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Цэс хайх..."
            aria-label="Цэс хайх"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Цэвэрлэх">
              <i className="bi bi-x-lg" />
            </button>
          )}
        </div>

        {groups.map((g) =>
          g.standalone ? (
            <div key={g.id} className="ts-nav-standalone">
              {g.links.map(renderLink)}
            </div>
          ) : (
            <div
              key={g.id}
              className={clsx('ts-nav-group', activeGroupId === g.id && 'has-active')}
            >
              <button
                type="button"
                className="ts-nav-group-head"
                onClick={() => toggleGroup(g.id)}
                aria-expanded={isGroupOpen(g)}
                title={g.label}
              >
                <span className="ts-nav-icon">
                  <i className={`bi bi-${g.icon}`} />
                </span>
                <span className="ts-nav-label">{g.label}</span>
                <span className="ts-nav-chevron">
                  <i className="bi bi-chevron-down" />
                </span>
              </button>
              <div className={clsx('ts-nav-group-body', isGroupOpen(g) && 'open')}>
                <div className="ts-nav-group-body-inner">
                  <div className="ts-nav-flyout-label">{g.label}</div>
                  {g.links.map(renderLink)}
                </div>
              </div>
            </div>
          ),
        )}

        {groups.length === 0 && <div className="ts-nav-empty">Илэрц олдсонгүй</div>}
      </nav>

      <div className="ts-sidebar-footer">
        <div className="ts-user-row">
          <div className="ts-avatar">{avatarText}</div>
          <div className="ts-user-info" style={{ flex: 1, minWidth: 0 }}>
            <div className="ts-user-name">{snapshot.displayName ?? 'Хэрэглэгч'}</div>
            <div className="ts-user-phone">+976 {phone || '—'}</div>
          </div>
          <button
            type="button"
            className="ts-toggle-btn"
            onClick={logout}
            title="Гарах"
            aria-label="Гарах"
          >
            <i className="bi bi-box-arrow-right" />
          </button>
        </div>
      </div>
    </aside>
  );
}
